import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  Sparkles, Camera, Ruler, Wand2, ArrowRight, Scale, BellOff, Link2,
  Store, Radio, Layers, Tags, FileSpreadsheet, Search, ShieldCheck, Bot,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProductCard from "@/components/ProductCard";
import HeroMarketScene from "@/components/HeroMarketScene";
import { navEntries, shopPath } from "@/constants/catalogCategories";
import { useLocale } from "@/context/LocaleContext";

const PILLARS = [
  {
    kicker: "Shop",
    title: "Compare, then buy",
    desc: "One item. Many stores. We rank Shoptosa sellers and partner merchants so you see the best price before you click out.",
    icon: Scale,
    href: "/shop",
    cta: "Compare prices",
  },
  {
    kicker: "Sell",
    title: "List once. Stay in sync.",
    desc: "Crosslist to eBay, Etsy, Poshmark and more. When it sells, sale detection delists the rest so you never double-sell.",
    icon: BellOff,
    href: "/auth?mode=register&role=seller",
    cta: "Start selling",
  },
  {
    kicker: "Earn",
    title: "Monetize every click-out",
    desc: "Partner catalogs and tracked buy links across affiliate networks. Discovery on Shoptosa. Checkout on the merchant. Commission back here.",
    icon: Link2,
    href: "/about",
    cta: "How we partner",
  },
];

const FEATURES = [
  { icon: Bot, title: "Agent monetization", desc: "Graph search and tracked buy links so AI agents can shop and earn on every click-out." },
  { icon: Sparkles, title: "AI search", desc: "Describe what you want. We pull matching items from sellers and partner stores across every category." },
  { icon: Wand2, title: "Virtual try-on", desc: "Upload a photo and preview an item on you before you commit." },
  { icon: Camera, title: "AI photoshoots", desc: "A flat lay becomes a studio shot — ready for the shop and every channel." },
  { icon: Ruler, title: "Fit confidence", desc: "Your measurements power size predictions across inconsistent brand charts." },
  { icon: Scale, title: "Price compare", desc: "PDP tables rank Shoptosa and merchant offers. Best in-stock price is marked." },
  { icon: Store, title: "Crosslist", desc: "One listing draft fills eBay, Etsy, Amazon, Poshmark, Mercari and more." },
  { icon: BellOff, title: "Auto-delist", desc: "A sale on one marketplace pulls the listing from the others automatically." },
  { icon: Tags, title: "Labels & stale alerts", desc: "Organize inventory, get warned when a listing goes cold, refresh in bulk." },
  { icon: FileSpreadsheet, title: "CSV & analytics", desc: "Export a multichannel inventory sheet. See what sold and where." },
  { icon: Layers, title: "Product graph", desc: "Same canonical item, many seller offers — ranked for shoppers and agents." },
  { icon: Link2, title: "Affiliate rails", desc: "Rakuten, CJ, Skimlinks and more wrap outbound buys with clean attribution." },
  { icon: Radio, title: "Live selling", desc: "Go live from your shop. Shoppers watch, ask, and buy the item in the room." },
];

const MARKETS = [
  "eBay", "Etsy", "Poshmark", "Mercari", "Depop", "Amazon", "Shopify",
  "Rakuten", "CJ", "Skimlinks", "Sovrn", "Flipkart", "Macy's", "Nordstrom",
];

const COMPARE_ROWS = [
  { store: "Shoptosa seller", price: "$128", note: "This listing", best: false, local: true },
  { store: "Partner · Nordstrom", price: "$119", note: "Checkout off-site", best: true, local: false },
  { store: "Partner · Macy's", price: "$134", note: "Checkout off-site", best: false, local: false },
  { store: "Shoptosa shop", price: "$141", note: "Ships from US", best: false, local: true },
];

const CHANNELS = ["eBay", "Etsy", "Poshmark", "Mercari"];

const fade = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };

function Reveal({ children, className = "" }) {
  return (
    <motion.div
      variants={fade}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function DelistDemo() {
  const [sold, setSold] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setSold((s) => !s), 4200);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="relative border border-aura-border bg-aura-bg/80 p-5 overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-px land-scan bg-gradient-to-b from-aura-cyan/70 to-transparent pointer-events-none" />
      <div className="flex items-center justify-between text-[10px] tracking-luxe uppercase text-aura-muted mb-4">
        <span>Sale detection</span>
        <span className={sold ? "text-aura-accent" : "text-aura-cyan"}>{sold ? "Sold · syncing" : "Watching"}</span>
      </div>
      <div className="text-sm font-medium mb-1">Emerald satin column</div>
      <p className="text-xs text-aura-muted mb-4">Listed on 4 channels. One sale. The rest come down.</p>
      <div className="grid grid-cols-2 gap-2">
        {CHANNELS.map((name, i) => {
          const winner = name === "eBay";
          const down = sold && !winner;
          return (
            <motion.div
              key={name}
              animate={{ opacity: down ? 0.35 : 1, scale: down ? 0.98 : 1 }}
              transition={{ delay: down ? i * 0.12 : 0, duration: 0.35 }}
              className={`px-3 py-2.5 border text-xs ${
                sold && winner
                  ? "border-aura-accent bg-aura-accent/10 text-white"
                  : "border-aura-border text-aura-muted"
              }`}
            >
              <div className="font-medium text-aura-ink">{name}</div>
              <div className="mt-0.5">
                {sold && winner ? "Sold · kept" : down ? "Auto-delisted" : "Live"}
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const { hash } = useLocation();
  const { country } = useLocale();
  const { data: categoryTree } = useQuery({
    queryKey: ["catalog-categories", country],
    queryFn: async () => (await api.get("/catalog/categories", { params: { country } })).data,
    staleTime: 5 * 60 * 1000,
  });
  const categories = navEntries(categoryTree)
    .filter((entry) => !(entry.dept && entry.category))
    .slice(0, 6);
  const { data: products = [] } = useQuery({
    queryKey: ["featured"],
    queryFn: async () => (await api.get("/products?limit=8")).data,
  });

  useEffect(() => {
    if (!hash) return;
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash]);

  return (
    <div className="overflow-x-hidden">
      <section className="relative -mt-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 min-h-0 lg:min-h-[100svh]">
          <div className="lg:col-span-5 flex flex-col justify-center px-6 lg:px-12 xl:px-16 pt-24 pb-10 order-2 lg:order-1 relative [container-type:inline-size]">
            <div className="hero-glow absolute top-1/4 left-0 w-[28rem] h-[28rem] rounded-full bg-[radial-gradient(circle,rgba(247,37,133,0.22),transparent_65%)] blur-2xl pointer-events-none" />
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="text-[11px] tracking-luxe uppercase text-aura-cyan mb-5 relative max-w-xs leading-relaxed">
              Monetization infrastructure for AI agents
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 }}
              className="font-serif text-[clamp(1.85rem,6.4cqi,3.15rem)] font-extrabold leading-[1.08] tracking-tight relative"
            >
              <span className="land-shine">Shop. Compare. Earn.</span>
              <br />
              <span className="land-shine">List. Sell. Everywhere.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.16 }}
              className="mt-5 text-sm sm:text-base text-aura-muted max-w-md leading-relaxed relative"
            >
              Finds the best price and never double-sells.
              Shoppers compare stores. Sellers list once. Agents search the graph
              and earn on the click-out.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.24 }}
              className="mt-7 flex flex-wrap gap-3 relative"
            >
              <Link to="/shop" className="px-8 py-3.5 rounded-full btn-gradient text-sm font-semibold flex items-center gap-2" data-testid="hero-shop-btn">
                Compare prices <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to={user ? "/tryon" : "/auth?mode=login"}
                className="px-8 py-3.5 rounded-full border border-white/20 text-sm font-semibold hover:border-aura-cyan hover:text-aura-cyan transition-colors"
                data-testid="hero-tryon-btn"
              >
                Try it on you
              </Link>
              {user?.role !== "seller" && (
                <Link
                  to="/developers"
                  className="px-8 py-3.5 rounded-full border border-white/20 text-sm font-semibold hover:border-aura-accent hover:text-aura-accent transition-colors"
                  data-testid="hero-agents-btn"
                >
                  Graph API
                </Link>
              )}
            </motion.div>
          </div>
          <div className="lg:col-span-7 relative order-1 lg:order-2 h-[68vh] lg:h-auto lg:min-h-[100svh]">
            <HeroMarketScene rows={COMPARE_ROWS} />
          </div>
        </div>
      </section>

      <div className="border-y border-aura-border bg-aura-surface/40 overflow-hidden py-3">
        <div className="land-marquee gap-10 px-6 text-[11px] tracking-luxe uppercase text-aura-muted">
          {[...MARKETS, ...MARKETS].map((m, i) => (
            <span key={`${m}-${i}`} className="shrink-0">{m}</span>
          ))}
        </div>
      </div>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
        <Reveal>
          <div className="text-[11px] tracking-luxe uppercase text-aura-muted mb-3">Shop every category</div>
          <h2 className="font-serif text-4xl tracking-tight max-w-2xl">Every category in one marketplace.</h2>
        </Reveal>
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-px bg-aura-border border border-aura-border">
          {categories.map((c) => (
            <Link
              key={c.key}
              to={shopPath(c)}
              className="bg-aura-bg px-4 py-4 text-sm hover:bg-aura-surface hover:text-aura-cyan transition-colors"
            >
              {c.label}
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <Reveal>
          <div className="text-[11px] tracking-luxe uppercase text-aura-muted mb-3">Three rails. One marketplace.</div>
          <h2 className="font-serif text-4xl lg:text-5xl tracking-tight max-w-2xl">
            Built like the tools you already trust — in one marketplace.
          </h2>
        </Reveal>
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-aura-border border border-aura-border"
        >
          {PILLARS.map((p) => (
            <motion.div key={p.title} variants={fade} className="bg-aura-bg p-8 hover:bg-aura-surface transition-colors">
              <p.icon className="w-6 h-6 text-aura-accent mb-5" strokeWidth={1.5} />
              <div className="text-[10px] tracking-luxe uppercase text-aura-cyan mb-2">{p.kicker}</div>
              <h3 className="font-serif text-2xl mb-3">{p.title}</h3>
              <p className="text-sm text-aura-muted leading-relaxed mb-6">{p.desc}</p>
              <Link to={p.href} className="text-sm link-underline inline-flex items-center gap-1.5">
                {p.cta} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </section>

      <section className="bg-aura-surface/30 border-y border-aura-border">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="text-[11px] tracking-luxe uppercase text-aura-cyan mb-3">Compare</div>
            <h2 className="font-serif text-4xl tracking-tight mb-4">See every store before you buy.</h2>
            <p className="text-aura-muted leading-relaxed mb-6">
              Like a price tracker, minus the clutter. The product page ranks Shoptosa sellers and
              partner merchants. Best in-stock price is marked. Partner checkout stays on their site —
              we just make the comparison honest.
            </p>
            <ul className="space-y-3 text-sm text-aura-muted">
              <li className="flex gap-2"><Search className="w-4 h-4 text-aura-accent shrink-0 mt-0.5" /> Search or open a listing — offers collapse into one table.</li>
              <li className="flex gap-2"><Scale className="w-4 h-4 text-aura-accent shrink-0 mt-0.5" /> Sort by price. Sold-out rows never win “best.”</li>
              <li className="flex gap-2"><ShieldCheck className="w-4 h-4 text-aura-accent shrink-0 mt-0.5" /> Comparison lives on Shoptosa — we don’t inject onto Amazon.</li>
            </ul>
            <Link to="/shop" className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full btn-accent text-sm font-semibold">
              Shop and compare <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
          <Reveal>
            <div className="border border-aura-border bg-aura-bg overflow-hidden">
              <div className="px-5 py-3 border-b border-aura-border flex justify-between text-[10px] tracking-luxe uppercase text-aura-muted">
                <span>Where to buy this piece</span>
                <span>4 stores</span>
              </div>
              {COMPARE_ROWS.map((row, i) => (
                <motion.div
                  key={row.store}
                  initial={{ opacity: 0, x: 16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className={`flex items-center justify-between px-5 py-4 border-t border-aura-border ${row.best ? "bg-aura-accent/5" : ""}`}
                >
                  <div>
                    <div className="text-sm font-medium flex items-center gap-2">
                      {row.store}
                      {row.best && <span className="text-[10px] uppercase tracking-luxe bg-aura-accent text-white px-1.5 py-0.5">Best price</span>}
                    </div>
                    <div className="text-xs text-aura-muted mt-0.5">{row.note}</div>
                  </div>
                  <div className="font-mono text-sm">{row.price}</div>
                </motion.div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal className="order-2 lg:order-1">
          <DelistDemo />
        </Reveal>
        <Reveal className="order-1 lg:order-2">
          <div className="text-[11px] tracking-luxe uppercase text-aura-cyan mb-3">Never double-sell</div>
          <h2 className="font-serif text-4xl tracking-tight mb-4">Sale detection. Auto-delist.</h2>
          <p className="text-aura-muted leading-relaxed mb-6">
            One inventory, many marketplaces.
            When an item sells, we pull it from the other channels so you don’t ship
            something you no longer have.
          </p>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            {[
              ["Master listing", "Edit once. Refresh across channels."],
              ["Custom labels", "Find the item in a 2,000-item inventory."],
              ["Stale warnings", "Relist before the algorithm forgets you."],
              ["Inventory CSV", "Taxes and books without the scavenger hunt."],
            ].map(([t, d]) => (
              <div key={t} className="border border-aura-border p-4">
                <div className="font-medium mb-1">{t}</div>
                <p className="text-aura-muted text-xs leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
          <Link
            to="/auth?mode=register&role=seller"
            className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full btn-gradient text-sm font-semibold"
            data-testid="become-seller-btn"
          >
            Become a seller <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </section>

      <section id="features" className="border-y border-aura-border bg-aura-surface/20 scroll-mt-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <Reveal>
            <div className="text-[11px] tracking-luxe uppercase text-aura-muted mb-3">Every capability</div>
            <h2 className="font-serif text-4xl tracking-tight mb-10">Highlighted in the product — and in the menu.</h2>
          </Reveal>
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.08 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-aura-border border border-aura-border"
          >
            {FEATURES.map((f, i) => {
              const last = i === FEATURES.length - 1;
              return (
                <motion.div
                  key={f.title}
                  variants={fade}
                  whileHover={{ y: -4 }}
                  className={`bg-aura-bg p-6 hover:bg-aura-surface transition-colors ${
                    last
                      ? "sm:col-span-2 lg:col-span-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6"
                      : ""
                  }`}
                >
                  <div className={last ? "max-w-xl" : ""}>
                    <f.icon className={`w-5 h-5 mb-4 ${i % 2 ? "text-aura-cyan" : "text-aura-accent"}`} strokeWidth={1.5} />
                    <h3 className="font-serif text-lg mb-2">{f.title}</h3>
                    <p className="text-sm text-aura-muted leading-relaxed">{f.desc}</p>
                    {last && (
                      <Link to="/live" className="mt-4 inline-flex items-center gap-1.5 text-sm link-underline">
                        Watch live rooms <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    )}
                  </div>
                  {last && (
                    <div className="relative w-full sm:max-w-sm h-28 border border-aura-border bg-aura-surface/60 overflow-hidden shrink-0">
                      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(247,37,133,0.22),transparent_55%)]" />
                      <div className="absolute top-3 left-3 flex items-center gap-2">
                        <span className="rounded-full bg-aura-accent text-white text-[10px] font-semibold tracking-luxe uppercase px-2.5 py-0.5 flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Live
                        </span>
                        <span className="text-[10px] text-aura-muted">3 watching</span>
                      </div>
                      <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                        <div>
                          <div className="text-xs font-medium">Studio drop</div>
                          <div className="text-[10px] text-aura-muted">Ask and buy in the room</div>
                        </div>
                        <div className="flex -space-x-1.5">
                          {["#F72585", "#4CC9F0", "#B5179E"].map((c) => (
                            <span key={c} className="w-6 h-6 rounded-full border border-aura-bg" style={{ background: c }} />
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="flex items-end justify-between mb-12">
          <Reveal>
            <div className="text-[11px] tracking-luxe uppercase text-aura-muted mb-3">From the marketplace</div>
            <h2 className="font-serif text-4xl tracking-tight">New arrivals</h2>
          </Reveal>
          <Link to="/shop" className="link-underline text-sm hidden sm:flex items-center gap-2">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-10">
          {products.slice(0, 8).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      <section className="relative overflow-hidden border-t border-aura-border">
        <div className="absolute inset-0 hero-glow bg-[radial-gradient(circle_at_20%_40%,rgba(247,37,133,0.18),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_60%,rgba(76,201,240,0.14),transparent_45%)]" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <h2 className="font-serif text-4xl lg:text-5xl tracking-tight mb-4">
              Shop smarter. Sell cleaner. <span className="gradient-text">Earn on the click-out.</span>
            </h2>
            <p className="text-aura-muted max-w-lg leading-relaxed">
              One product graph for buyers. One inventory OS for sellers. Affiliate rails for
              partner listings. That’s Shoptosa — a marketplace, not a pile of tools.
            </p>
          </Reveal>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <Link to="/shop" className="px-8 py-3.5 rounded-full btn-gradient text-sm font-semibold">Explore the shop</Link>
            <Link to="/tryon" className="px-8 py-3.5 rounded-full border border-white/20 text-sm font-semibold hover:border-aura-cyan">Try it on</Link>
            <Link to="/auth?mode=register&role=seller" className="px-8 py-3.5 rounded-full border border-white/20 text-sm font-semibold hover:border-aura-accent">Become a seller</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
